import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAddItemMutation, useUpdateItemMutation } from "../../../hooks/base-hooks";
import { fetchItem } from "../../../support/queries";
import { CacheKey } from "../../../types/cache-keys";
import { ProductAppliance } from "../../../types/product";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  productId: number;
  applianceId?: number;
  onClose: () => void;
  onSaved: (savedAppliance?: ProductAppliance) => void;
  readOnly?: boolean;
};

type ProductApplianceForm = {
  name: string;
  description: string;
  end_of_life: string;
  url: string;
  spec_summary: string;
  deployment_type: string;
  part_number: string;
};

const emptyForm: ProductApplianceForm = {
  name: "",
  description: "",
  end_of_life: "",
  url: "",
  spec_summary: "",
  deployment_type: "",
  part_number: "",
};

const normalizeDate = (value?: string | Date | null) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  try {
    return value.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

export default function ProductApplianceEditorDialog({
                                                       open,
                                                       mode,
                                                       productId,
                                                       applianceId,
                                                       onClose,
                                                       onSaved,
                                                       readOnly = false,
                                                     }: Props) {
  const isEdit = mode === "edit" && !!applianceId;

  const addAppliance = useAddItemMutation("db/product-appliance", () => [
    CacheKey.ProductAppliance,
    CacheKey.Product,
    productId,
  ]);

  const updateAppliance = useUpdateItemMutation("db/product-appliance", () => [
    CacheKey.ProductAppliance,
    CacheKey.Product,
    productId,
    String(applianceId),
  ]);

  const [form, setForm] = useState<ProductApplianceForm>(emptyForm);
  const [initialForm, setInitialForm] = useState<ProductApplianceForm>(emptyForm);
  const [loadingAppliance, setLoadingAppliance] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    let active = true;

    const loadAppliance = async () => {
      if (!open) return;

      if (!isEdit || !applianceId) {
        setForm(emptyForm);
        setInitialForm(emptyForm);
        return;
      }

      setLoadingAppliance(true);
      try {
        const data = await fetchItem(`db/product-appliance/${applianceId}`, { detailed: true });

        if (!active) return;

        const nextForm: ProductApplianceForm = {
          name: data?.name ?? "",
          description: data?.description ?? "",
          end_of_life: normalizeDate(data?.end_of_life),
          url: data?.url ?? "",
          spec_summary: data?.spec_summary ?? "",
          deployment_type: data?.deployment_type ?? "",
          part_number: data?.part_number ?? "",
        };

        setForm(nextForm);
        setInitialForm(nextForm);
      } catch (error) {
        console.error("Failed to load product appliance:", error);
      } finally {
        if (active) setLoadingAppliance(false);
      }
    };

    loadAppliance();

    return () => {
      active = false;
    };
  }, [open, isEdit, applianceId]);

  const saving = addAppliance.isPending || updateAppliance.isPending;
  const disabledAll = saving || loadingAppliance || readOnly;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.description !== initialForm.description ||
      form.end_of_life !== initialForm.end_of_life ||
      form.url !== initialForm.url ||
      form.spec_summary !== initialForm.spec_summary ||
      form.deployment_type !== initialForm.deployment_type ||
      form.part_number !== initialForm.part_number
    );
  }, [form, initialForm]);

  const handleSave = async () => {
    if (readOnly) return;

    const payload = {
      product: productId,
      name: form.name,
      description: form.description || null,
      end_of_life: form.end_of_life || null,
      url: form.url || null,
      spec_summary: form.spec_summary || null,
      deployment_type: form.deployment_type || null,
      part_number: form.part_number || null,
    };

    try {
      if (!isEdit) {
        const created: any = await addAppliance.mutateAsync(payload);
        onClose();
        setTimeout(() => onSaved(created), 0);
        return;
      }

      if (applianceId) {
        const updated: any = await updateAppliance.mutateAsync({
          ...payload,
          id: applianceId,
        });
        onClose();
        setTimeout(() => onSaved(updated), 0);
      }
    } catch (error) {
      console.error("Failed to save product appliance:", error);
    }
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const title = readOnly
    ? !isEdit
      ? "View Product Appliance"
      : `View Appliance: ${form.name || `#${applianceId}`}`
    : !isEdit
      ? "Create Product Appliance"
      : `Edit Appliance: ${form.name || `#${applianceId}`}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          {loadingAppliance && (
            <Box display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={18} />
              Loading...
            </Box>
          )}

          <TextField
            fullWidth
            required
            margin="normal"
            label="Name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            multiline
            minRows={3}
            disabled={disabledAll}
          />

          <FormControl fullWidth margin="normal" disabled={disabledAll}>
            <InputLabel>Deployment Type</InputLabel>
            <Select
              value={form.deployment_type}
              label="Deployment Type"
              onChange={(e) => setForm((p) => ({ ...p, deployment_type: e.target.value }))}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="physical">Physical</MenuItem>
              <MenuItem value="virtual">Virtual</MenuItem>
            </Select>
          </FormControl>

          {isEdit && (
            <TextField
              fullWidth
              margin="normal"
              type="date"
              label="End of Life"
              InputLabelProps={{ shrink: true }}
              value={form.end_of_life}
              onChange={(e) => setForm((p) => ({ ...p, end_of_life: e.target.value }))}
              disabled={disabledAll}
            />
          )}

          <TextField
            fullWidth
            margin="normal"
            label="URL"
            value={form.url}
            onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Spec Summary"
            value={form.spec_summary}
            onChange={(e) => setForm((p) => ({ ...p, spec_summary: e.target.value }))}
            multiline
            minRows={3}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Part Number"
            value={form.part_number}
            onChange={(e) => setForm((p) => ({ ...p, part_number: e.target.value }))}
            disabled={disabledAll}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !form.name}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message="You have unsaved changes in this product appliance editor. If you continue, your changes will be lost."
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={resetAndClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  );
}